/*
Created by: Artur Haber
Usage example:
import Lightbox from 'src/components/Lightbox'
import Image from 'next/image'
export default function Page() {
  return (
    <Lightbox imageSrc={imageBig} alt={alt}>
      <Image className="" src={image} alt="" />
    </Lightbox>
  );
}
*/
'use client'

import { useEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'
import Modal from 'react-modal'
import Image from 'next/image'
import { useLenis } from 'lenis/react'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import Typography from 'src/components/design-system/Typography'
import Close from 'public/icons/interface/close.svg'
Modal.setAppElement('body')

export default function Lightbox({ children, imageSrc, alt, className, imageClassName = '', width, height }) {
  const [open, setOpen] = useState(false)
  const [startAnimation, setStartAnimation] = useState(false)
  const [finishOpen, setFinishOpen] = useState(false)
  const contentRef = useRef(null)
  const triggerRef = useRef(null)
  const translate = useTranslateEntry()
  const lenis = useLenis()
  const handleAfterOpenFunc = () => setStartAnimation(true)
  const handleAfterCloseFunc = () => setStartAnimation(false)

  useEffect(() => {
    if (open) {
      lenis?.stop()
    } else {
      lenis?.start()
    }
    if (!open) {
      setStartAnimation(false)
      setFinishOpen(false)
    }
    const timeout = setTimeout(() => {
      if (open) setFinishOpen(true)
    }, 100)
    return () => clearTimeout(timeout)
  }, [open])
  useEffect(() => {
    if (!contentRef.current) return

    if (finishOpen) {
      gsap.fromTo(
        contentRef.current,
        { opacity: 0, scale: 0.9 },
        { opacity: 1, scale: 1, duration: 0.5, ease: 'power3.out' },
      )
    } else {
      gsap.to(contentRef.current, { opacity: 0, scale: 0.9, duration: 0.4, ease: 'power3.in' })
    }
  }, [finishOpen])

  const closeLightbox = () => setOpen(false)
  const handleKey = (e) => e.key === 'Escape' && closeLightbox()

  return (
    <>
      <button
        ref={triggerRef}
        role="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => e.key === 'Enter' && setOpen(true)}
        className={`cursor-pointer ${className}`}
      >
        {children}
      </button>

      <Modal
        isOpen={open}
        onRequestClose={closeLightbox}
        onAfterOpen={handleAfterOpenFunc}
        onAfterClose={handleAfterCloseFunc}
        style={{
          content: {
            border: 0,
            inset: 0,
            padding: 0,
            margin: 0,
            width: '100%',
            height: '100%',
            overflow: 'visible',
            background: 'transparent',
          },
          overlay: {
            zIndex: 1000,
            width: '100%',
            background: startAnimation ? '#17171880' : 'transparent',
            backdropFilter: startAnimation ? 'blur(12.699999809265137px)' : 'none',
            transition: open
              ? 'background 0.3s ease-in-out, backdrop-filter 0.3s ease-in-out'
              : 'background 0.3s ease-in-out 0.5s, backdrop-filter 0.3s ease-in-out 0.5s',
          },
        }}
        contentLabel={translate('T_FULLSCREEN_PREVIEW')}
        shouldCloseOnOverlayClick={true}
        aria={{ labelledby: 'lightbox-title' }}
      >
        <div
          ref={contentRef}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          id="lightbox-dialog"
          onKeyDown={handleKey}
          className="w-full bg-basic-white max-w-[958px] max-h-screen opacity-0 scale-90 outline-none overflow-auto px-12 d:px-32 pt-24 pb-64 flex flex-col items-end gap-y-32 rounded-[4px] fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          data-lenis-prevent
        >
          <h2 id="lightbox-title" className="sr-only">
            {translate('T_FULLSCREEN_PREVIEW')}
          </h2>

          <button
            onClick={closeLightbox}
            aria-label={translate('T_CLOSE_FULLSCREEN_PREVIEW')}
            className="inline-flex items-center gap-x-8 cursor-pointer group"
          >
            <Typography
              variant="h3-bold"
              component="span"
              color="text-basic-black"
              className="block w-fit h-fit relative after:content-[''] after:h-1 after:w-0 after:transition-[width] after:absolute after:bottom-0 after:left-0 group-hover:after:w-full after:bg-basic-black"
            >
              {translate('T_CLOSE')}
            </Typography>
            <Close />
          </button>
          <div className="w-full aspect-[894/577] max-w-full">
            <Image
              className={`w-full h-full object-contain object-center ${imageClassName}`}
              src={imageSrc}
              alt={alt}
              width={width}
              height={height}
            />
          </div>
        </div>
      </Modal>
    </>
  )
}
